import { useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { Form, Formik } from 'formik';

import { Flex } from 'src/components/Flex';
import {
    GET_APPLICATION,
    GET_APPLICATION_BY_SERVICE_ID,
} from 'src/graphql/queries/merchantApplications.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';

import { CancelMerchantAction, CancelMerchantSearch } from './components';
import { ApplicationIDValidationSchema, isNumeric } from './validation/ApplicationIDValidation';

const CancelMerchantPage = () => {
    const [isValidationTriggered, setIsValidationTriggered] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const [selectedType, setSelectedType] = useState<'application' | 'service' | undefined>(
        undefined
    );
    const [resolvedApplicationId, setResolvedApplicationId] = useState<string | null>(null);

    const [fetchApplication, { loading: applicationLoading, data }] = useLazyQuery(GET_APPLICATION);
    const [fetchApplicationByService, { loading: serviceLoading }] = useLazyQuery(
        GET_APPLICATION_BY_SERVICE_ID
    );

    const initialValues = {
        idInput: '',
    };

    const validateApplicationId = async (idInput: string): Promise<boolean> => {
        setIsValidationTriggered(true);

        let applicationId = idInput;

        try {
            if (selectedType === 'service') {
                const merchantIDResult = await fetchApplicationByService({
                    variables: { serviceId: parseInt(idInput, 10) },
                    fetchPolicy: 'network-only',
                });
                if (!merchantIDResult.data?.getApplicationByServiceId) {
                    setValidationError('No application found for the provided Service ID.');
                    setValidationError(null);
                    return false;
                }

                applicationId = merchantIDResult.data.getApplicationByServiceId;
                setResolvedApplicationId(applicationId);
            } else {
                setResolvedApplicationId(idInput);
            }
            const result = await fetchApplication({
                variables: { id: parseInt(applicationId, 10) },
                fetchPolicy: 'network-only',
            });

            if (!result.data?.retrieveMerchantApplication) {
                setValidationError('No data found for the provided Application ID.');
                setValidationError(null);
                return false;
            }

            return true;
        } catch (error) {
            setValidationError('An error occurred while validating the ID.');
            return false;
        }
    };

    return (
        <PageContent heading="Cancel Merchant">
            <Formik
                initialValues={initialValues}
                validationSchema={ApplicationIDValidationSchema}
                onSubmit={() => {}}
                validateOnChange
                context={{ isValidationTriggered }}
            >
                {({ values, errors, isSubmitting }) => {
                    const isButtonEnabled = values.idInput.length >= 1 && isNumeric(values.idInput);
                    const notificationId = values.idInput;

                    return (
                        <Form>
                            <Flex flexDirection="column" gap="24px">
                                <CancelMerchantSearch
                                    selectedType={selectedType}
                                    setSelectedType={setSelectedType}
                                    showValidationError={isValidationTriggered && errors.idInput}
                                />

                                <CancelMerchantAction
                                    isButtonEnabled={isButtonEnabled}
                                    isSubmitting={isSubmitting}
                                    applicationId={resolvedApplicationId || values.idInput}
                                    selectedType={selectedType}
                                    validateApplicationId={() => {
                                        return validateApplicationId(values.idInput);
                                    }}
                                    loading={applicationLoading || serviceLoading}
                                    validationError={validationError}
                                    merchantData={data?.retrieveMerchantApplication}
                                    notificationId={notificationId}
                                />
                            </Flex>
                        </Form>
                    );
                }}
            </Formik>
        </PageContent>
    );
};

export default CancelMerchantPage;
